import {
  Checkbox,
  FontColorEnum,
  FormField,
  FormHint,
  TIcon,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { FormEvent, ReactElement } from 'react';
import { Controller, FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type TCheckboxFormProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  classname?: string;
  icon?: ReactElement<TIcon, TIcon>;
  readOnly?: boolean;
  mapChange?: (
    onChange: (event: FormEvent<HTMLInputElement>) => void,
    event: FormEvent<HTMLInputElement>
  ) => void;
};

export function CheckboxForm<T extends FieldValues>({
  form,
  name,
  required,
  disabled,
  label,
  placeholder,
  classname,
  icon,
  readOnly,
  mapChange,
}: TCheckboxFormProps<T>) {
  const { t } = useTranslation();
  const { control } = form;
  const labelSuffix = required ? '*' : '';

  return (
    <FormField>
      <Controller
        disabled={disabled}
        name={name}
        control={control}
        rules={{
          required: required ? { value: true, message: t('El campo es obligatorio') } : false,
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <div className={clsx('flex w-full justify-between gap-4', classname)}>
              <div className="flex items-center gap-2">
                {icon && icon}
                <Typography>
                  {placeholder || label}
                  {labelSuffix}
                </Typography>
              </div>
              <Checkbox
                checked={value}
                disabled={disabled}
                value={value}
                onChange={e => {
                  !readOnly ? (mapChange ? mapChange(onChange, e) : onChange(e)) : undefined;
                }}
                iconClassName={
                  readOnly ? '!bg-black/[.05] !text-text-dark-10/[.25] !border-black/[.05]' : ''
                }
                labelClassName={readOnly ? '!cursor-not-allowed' : ''}
              />
            </div>
            {error && (
              <FormHint color={FontColorEnum.Error20} type="error">
                {error.message}
              </FormHint>
            )}
          </>
        )}
      />
    </FormField>
  );
}
